import {SLASH} from '@angular/cdk/keycodes';
import {Component} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  shareReplay,
  startWith,
  take,
  withLatestFrom
} from 'rxjs/operators';

import {Location} from '../atlas_sdk/api';
import {BusinessNavConfigService, Config} from '../config.service';
import {ModalService} from '../core-components/modal/modal.service';
import {SidebarService} from '../core-components/sidebar/sidebar.service';
import {BusinessNavDataService, LocationCounts} from '../data.service';
import {BusinessNavKeybindService} from '../keybind.service';
import {LocationSwitcherModalComponent} from '../modals/location-switcher-modal/location-switcher-modal.component';
import {BusinessNavLocationService} from '../modals/location-switcher-modal/location.service';


interface CachedLocationResponse {
  locationId: string;
  response: Location;
}

@Component({
  selector: 'atlas-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss']
})
export class LocationSelectorComponent {
  interactable$: Observable<boolean>;
  loadedLocations$: Observable<Location[]>;
  currentLocation$: Observable<Location>;
  currentLocationName$: Observable<string>;
  currentLocationAddress$: Observable<string>;
  showIcon$: Observable<boolean>;

  constructor(
    private configService: BusinessNavConfigService,
    private locationService: BusinessNavLocationService,
    private dataService: BusinessNavDataService,
    private modalService: ModalService,
    private keybindService: BusinessNavKeybindService,
    private sidebarService: SidebarService) {
    this.loadedLocations$ = this.locationService.selectedLocationList$;

    const locationConfig$ = combineLatest(this.configService.config$, this.loadedLocations$).pipe(
      publishReplay(1),
      refCount()
    );
    this.currentLocation$ = locationConfig$.pipe(
      map(([config, locations]: [Config, Location[]]) => {
        for (let i = 0; i < locations.length; i++) {
          // Continue if current location not a match
          if (
            (!locations[i].accountGroup || config.accountId !== locations[i].accountGroup.accountGroupId) &&
            (!locations[i].brand || config.groupPath !== locations[i].brand.pathNodes.join('|'))) {
            continue;
          }
          const cache = {
            locationId: this.locationIdFromConfig(config),
            response: locations[i]
          } as CachedLocationResponse;
          this.setLocationCache(cache);
          return cache;
        }
      }),
      startWith(this.getLastLocation()),
      withLatestFrom(this.configService.config$),
      filter(([res, config]: [CachedLocationResponse, Config]) => !!res && res.locationId === this.locationIdFromConfig(config)),
      map(([res, _]: [CachedLocationResponse, Config]) => res.response),
      publishReplay(1),
      refCount()
    );
    this.currentLocationName$ = this.currentLocation$.pipe(
      distinctUntilChanged(),
      map((location: Location) => this.getLocationName(location)),
      filter((name: string) => !!name),
      publishReplay(1),
      refCount()
    );
    this.currentLocationAddress$ = this.currentLocation$.pipe(
      distinctUntilChanged(),
      map((location: Location) => this.getLocationAddress(location)),
      filter((address: string) => !!address),
      publishReplay(1),
      refCount()
    );

    this.keybindService.keyEvent$.subscribe((event: KeyboardEvent) => {
      const element = (event.target as HTMLElement);
      const tag = element.tagName.toLowerCase();
      const userIsTyping = tag === 'input' || tag === 'textarea';
      if (!userIsTyping && event.shiftKey && event.keyCode === SLASH) {
        this.showSwitchLocationDialog();
      }
    });

    this.interactable$ = this.dataService.locationCounts$.pipe(
      map((locations) => {
        return ((locations.businesses ? locations.businesses : 0) + (locations.brands ? locations.brands : 0) > 1 || locations.businesses === -1 || locations.brands === -1);
      }),
      shareReplay(1)
    );

    this.showIcon$ = this.sidebarService.showMenuToggle$.pipe(
      map((isVisible: boolean) => !isVisible),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private locationIdFromConfig(cfg: Config): string {
    return (!!cfg.accountId) ? cfg.accountId : cfg.groupPath;
  }

  setLocationCache(cache: CachedLocationResponse): void {
    localStorage.setItem('business-nav-current-location', JSON.stringify({
      'locationId': cache.locationId,
      'response': cache.response.toApiJson(),
    }));
  }

  private getLastLocation(): CachedLocationResponse {
    const o: { locationId: string, response: object } = JSON.parse(localStorage.getItem('business-nav-current-location'));
    if (!o || !o.response) {
      return null;
    }
    return {
      locationId: o.locationId,
      response: Location.fromProto(o.response),
    };
  }

  getLocationName(location: Location): string {
    if (!location) {
      return '';
    }
    if (!!location.accountGroup) {
      return location.accountGroup.name;
    }
    if (!!location.brand) {
      return location.brand.name;
    }
    return '';
  }

  getLocationAddress(location: Location): string {
    if (!location) {
      return '';
    }
    if (!!location.accountGroup) {
      return location.accountGroup.address;
    }
    if (!!location.brand) {
      return 'Brand';
    }
    return '';
  }

  showSwitchLocationDialog(): void {
    this.modalService.open(LocationSwitcherModalComponent);
  }
}
